import React, { useState, useEffect, useRef } from 'react';
import { TalkingHead } from "../../modules/talkinghead";
import Rocket from "../rocket/rocket";
import "../login/login-page.css";

const INTRO_TEXT =
  "Hi, I am Lara, your personal tutor. We're gonna be great friends. " +
  "I want to give you the best education possible. I'm going to help you learn whatever you want — " +
  "from space science to plants to the rules of an unknown sport. " +
  "And it's going to be a fun ride. I look forward to getting to know you. Are you ready to start?";

const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria",
  "Bangladesh", "Belgium", "Bolivia", "Brazil", "Cambodia", "Canada", "Chile",
  "China", "Colombia", "Croatia", "Czech Republic", "Denmark", "Ecuador",
  "Egypt", "Ethiopia", "Finland", "France", "Germany", "Ghana", "Greece",
  "Guatemala", "Hungary", "India", "Indonesia", "Iran", "Iraq", "Ireland",
  "Israel", "Italy", "Japan", "Jordan", "Kenya", "Malaysia", "Mexico",
  "Morocco", "Myanmar", "Nepal", "Netherlands", "New Zealand", "Nigeria",
  "Norway", "Pakistan", "Peru", "Philippines", "Poland", "Portugal",
  "Romania", "Russia", "Saudi Arabia", "Senegal", "Singapore", "South Africa",
  "South Korea", "Spain", "Sri Lanka", "Sudan", "Sweden", "Switzerland",
  "Tanzania", "Thailand", "Tunisia", "Turkey", "Uganda", "Ukraine",
  "United Arab Emirates", "United Kingdom", "United States", "Uruguay",
  "Venezuela", "Vietnam", "Yemen", "Zimbabwe"
];

const GRADES = [
  "Pre-K",
  "Kindergarten",
  "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5",
  "Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12",
  "College / University",
  "Adult Learner",
];

const AGES = Array.from({ length: 78 }, (_, i) => i + 3); // 3 – 80

// ─── styles ──────────────────────────────────────────────────────────────────

const inputStyle = {
  width: "100%",
  padding: "10px 16px",
  borderRadius: "10px",
  border: "1px solid rgba(255,255,255,0.2)",
  background: "rgba(255,255,255,0.08)",
  color: "white",
  fontSize: "15px",
  outline: "none",
  boxSizing: "border-box",
  marginTop: "8px",
};

const labelStyle = {
  color: "rgba(255,255,255,0.55)",
  fontSize: "11px",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  display: "block",
};

const btnStyle = {
  marginTop: "16px",
  width: "100%",
  padding: "12px",
  borderRadius: "10px",
  border: "none",
  background: "rgba(99,179,237,0.85)",
  color: "white",
  fontSize: "14px",
  fontWeight: 700,
  letterSpacing: "1px",
  cursor: "pointer",
  transition: "opacity 0.2s",
};

const speakingIndicatorStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "10px",
  padding: "8px 0",
};

// ─── component ───────────────────────────────────────────────────────────────

const Welcome = ({ profile, logOut, onComplete, userId }) => {
  // step: "loading" | "intro" | "nickname" | "age" | "grade" | "country" | "complete"
  const [step, setStep] = useState("loading");
  const [inputVisible, setInputVisible] = useState(false);
  const [avatarLoadingProgress, setAvatarLoadingProgress] = useState(0);
  const [avatarLoading, setAvatarLoading] = useState(true);

  // form fields
  const [nickname, setNickname] = useState("");
  const [age, setAge] = useState("");
  const [grade, setGrade] = useState("");
  const [country, setCountry] = useState("");

  const headRef = useRef(null);
  // Prevents React StrictMode's double-invoke from running init() twice.
  // Refs survive the strict-mode unmount/remount cycle, so this is set once.
  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    const init = async () => {
      const nodeAvatar = document.getElementById('welcome-avatar');
      if (!nodeAvatar) return;

      const head = new TalkingHead(nodeAvatar, {
        ttsEndpoint: "https://texttospeech.googleapis.com/v1beta1/text:synthesize",
        ttsApikey: "",
        lipsyncModules: ["en"],
        cameraView: "upper",
      });
      headRef.current = head;

      await head.showAvatar({
        url: 'avaturn.glb',
        body: 'F',
        avatarMood: 'happy',
        ttsLang: "en-GB",
        ttsVoice: "en-GB-Standard-A",
        lipsyncLang: 'en',
      }, (ev) => {
        if (ev.lengthComputable)
          setAvatarLoadingProgress(Math.min(100, Math.round(ev.loaded / ev.total * 100)));
      });

      // Warm, upbeat mood throughout the welcome flow
      head.setMood('happy');

      setAvatarLoading(false);

      // Step 1: Intro — ✋ triggers a wave gesture at the greeting
      setStep("intro");
      await head.speakText(
        "Hi! I am Lara, your personal tutor. We're gonna be great friends. I want to give you the best education possible. I'm going to help you learn whatever you want from space science to plants to the rules of an unknown sport. And it's going to be a fun ride. I look forward to getting to know you. Are you ready to start?"
      );

      // Step 2: Ask for nickname
      setStep("nickname");
      await head.speakText("Let's get started! What should I call you?");
      setInputVisible(true);
    };

    init().catch(console.error);

    return () => {
      // If the component truly unmounts, stop any playing audio.
      if (headRef.current) {
        headRef.current.stopSpeaking();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Advance to the next step: speak prompt, then reveal input
  const advance = async (nextStep, prompt) => {
    setInputVisible(false);
    setStep(nextStep);
    if (headRef.current && prompt) {
      await headRef.current.speakText(prompt);
    }
    if (nextStep !== "complete") {
      setInputVisible(true);
    }
  };

  const handleNickname = () => {
    if (!nickname.trim()) return;
    advance("age", `${nickname.trim()}! What a great name. How old are you?`);
  };

  const handleAge = () => {
    if (!age) return;
    advance("grade", `${age} years old — perfect! What grade are you in?`);
  };

  const handleGrade = () => {
    if (!grade) return;
    advance("country", `${grade} — love it! And where are you from?`);
  };

  const handleCountry = async () => {
    if (!country) return;
    const userData = { nickname: nickname.trim(), age, grade, country };
    setStep("complete");
    setInputVisible(false);
    if (headRef.current) {
      await headRef.current.speakText(
        `${country}! Wonderful. I'm so excited to be your tutor, ${nickname.trim()}. Let's go!`
      );
    }
    try {
      const token = localStorage.getItem("google_token");
      await fetch('https://plato-vm.westus.cloudapp.azure.com:5001/Transcript/SaveUserProfile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          userId: userId || '',
          email: profile?.email || '',
          nickname: nickname.trim(),
          age,
          grade,
          country
        })
      });
    } catch (e) {
      console.error('Failed to save profile', e);
    }
    onComplete(userData);
  };

  // ── render helpers ──────────────────────────────────────────────────────────

  const SpeakingIndicator = () => (
    <div style={speakingIndicatorStyle}>
      <div className="spinner" />
    </div>
  );

  const renderContent = () => {
    if (step === "loading") {
      return (
        <div style={speakingIndicatorStyle}>
          <div className="spinner" />
          <p className="progress-text">LARA {avatarLoadingProgress}% LOADED</p>
        </div>
      );
    }

    if (step === "intro" || !inputVisible) {
      return null;
    }

    if (step === "nickname") {
      return (
        <div style={{ width: "100%" }}>
          <label style={labelStyle}>Your nickname</label>
          <input
            style={inputStyle}
            type="text"
            placeholder="e.g. Alex"
            value={nickname}
            onChange={e => setNickname(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleNickname()}
            autoFocus
          />
          <button style={btnStyle} className="btn-pulse" onClick={handleNickname}>
            Continue →
          </button>
        </div>
      );
    }

    if (step === "age") {
      return (
        <div style={{ width: "100%" }}>
          <label style={labelStyle}>Your age</label>
          <select style={inputStyle} value={age} onChange={e => setAge(e.target.value)}>
            <option value="" disabled>Select your age</option>
            {AGES.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button style={{ ...btnStyle, opacity: age ? 1 : 0.5 }} className={age ? "btn-pulse" : ""} onClick={handleAge} disabled={!age}>
            Continue →
          </button>
        </div>
      );
    }

    if (step === "grade") {
      return (
        <div style={{ width: "100%" }}>
          <label style={labelStyle}>Your grade</label>
          <select style={inputStyle} value={grade} onChange={e => setGrade(e.target.value)}>
            <option value="" disabled>Select your grade</option>
            {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <button style={{ ...btnStyle, opacity: grade ? 1 : 0.5 }} className={grade ? "btn-pulse" : ""} onClick={handleGrade} disabled={!grade}>
            Continue →
          </button>
        </div>
      );
    }

    if (step === "country") {
      return (
        <div style={{ width: "100%" }}>
          <label style={labelStyle}>Your country</label>
          <select style={inputStyle} value={country} onChange={e => setCountry(e.target.value)}>
            <option value="" disabled>Select your country</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button style={{ ...btnStyle, opacity: country ? 1 : 0.5 }} className={country ? "btn-pulse" : ""} onClick={handleCountry} disabled={!country}>
            Let's go! →
          </button>
        </div>
      );
    }

    if (step === "complete") {
      return null;
    }

    return null;
  };

  // ── layout ──────────────────────────────────────────────────────────────────

  const stepLabels = {
    loading:  null,
    intro:    null,
    nickname: "Nice to meet you!",
    age:      `Hi, ${nickname || "there"}!`,
    grade:    `Got it — ${age} years old.`,
    country:  `${grade} — great!`,
    complete: null,
  };

  return (
    <div style={{
      display: "flex",
      flexDirection: "row",
      alignItems: "stretch",
      justifyContent: "center",
      height: "100vh",
      overflow: "hidden",
      backgroundImage: "url('https://platovideo.z13.web.core.windows.net/brightResized.png')",
      backgroundSize: "100% 100%",
    }}>
      <Rocket />

      {/* ── Left: conversation panel ── */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        flex: "0 0 auto",
        width: "360px",
        padding: "0 40px",
      }}>
        {/* Branding */}
        <div style={{ marginBottom: "28px" }}>
          <h1 style={{
            color: "white",
            fontSize: "36px",
            fontWeight: 700,
            letterSpacing: "6px",
            margin: "0 0 4px",
          }}>PLATO</h1>
          <p style={{
            color: "rgba(255,255,255,0.4)",
            fontSize: "10px",
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            margin: 0,
          }}>YOUR ONLY VIRTUAL TUTOR</p>
        </div>

        {/* Context label (e.g. "Hi, Alex!") */}
        {stepLabels[step] && (
          <p style={{
            color: "rgba(255,255,255,0.7)",
            fontSize: "16px",
            fontWeight: 600,
            margin: "0 0 16px",
          }}>
            {stepLabels[step]}
          </p>
        )}

        {/* Glass card */}
        <div style={{
          width: "100%",
          padding: "28px",
          borderRadius: "20px",
          background: "rgba(255,255,255,0.07)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 16px 40px rgba(0,0,0,0.3)",
          boxSizing: "border-box",
          minHeight: "120px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          {renderContent()}
        </div>
      </div>

      {/* ── Right: avatar ── */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
        flex: "0 0 auto",
        width: "500px",
        height: "100vh",
        overflow: "hidden",
      }}>
        <div id="welcome-avatar" style={{ width: "500px", height: "600px", flexShrink: 0 }} />
      </div>
    </div>
  );
};

export default Welcome;
